import { Box, Stack } from '@mui/material'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { AreaValue, AreaValueDataObject, EmptyCell, Percentage, subtractAreaValueDataObjects, Table, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { isParkingType, TENANT_USAGE_TYPE_LABELS, TenantUsageTypeLabelKeys } from 'ComponentLibrary/src/common/tenant'
import {
    AreaSummaryCell,
    SummaryCellContainer,
    TenantSummaryHeaderCell,
    UnitsSummaryCell,
} from 'ComponentLibrary/src/widgets/TenantTables/TenantTableCell'

type TenantSummaryItemValues = {
    type?: TenantUsageTypeLabelKeys | 'override_total'
    number_of_units?: number
    vacancy_number_of_units?: number
    total_area?: AreaValueDataObject
    vacancy_area?: AreaValueDataObject
}

type SummaryGroupKey = 'rentable' | 'rented' | 'vacant'

type GroupValues<TValue> = {
    baseValue?: TValue
    valueCheck?: TValue
    change?: number
    delta?: number
    hasDiff: boolean
}

type AreaSummaryRow = {
    label: string
    valueType: 'area'
    groups: Record<SummaryGroupKey, GroupValues<AreaValueDataObject>>
}

type NumberSummaryRow = {
    label: string
    valueType: 'units' | 'parking'
    groups: Record<SummaryGroupKey, GroupValues<number>>
}

type ValueCheckTenantSummaryRow = AreaSummaryRow | NumberSummaryRow

const summaryGroups: SummaryGroupKey[] = ['rentable', 'rented', 'vacant']

const shouldShowGroupSeparator = (groupKey: SummaryGroupKey) => groupKey !== 'rentable'

const columnHelper = createColumnHelper<ValueCheckTenantSummaryRow>()

export type ValueCheckTenantSummaryWidgetCopy = {
    title: string
    categoryLabel: string
    baseLabel: string
    valueCheckLabel: string
    changeLabel: string
    groupLabels: Record<SummaryGroupKey, string>
    rowLabels: {
        area: string
        units: string
        parkingSpaces: string
    }
    usageTypeLabels: Record<TenantUsageTypeLabelKeys, string>
    getUnitLabel: (count: number, isParking: boolean) => string
}

const DEFAULT_VALUE_CHECK_TENANT_SUMMARY_WIDGET_COPY: ValueCheckTenantSummaryWidgetCopy = {
    title: 'Mieterliste',
    categoryLabel: 'Kategorie',
    baseLabel: 'Basis',
    valueCheckLabel: 'Wertüberprüfung',
    changeLabel: 'Veränderung',
    groupLabels: {
        rentable: 'vermietbar',
        rented: 'vermietet',
        vacant: 'leerstand',
    },
    rowLabels: {
        area: 'Flächen',
        units: 'Einheiten',
        parkingSpaces: 'Stellplätze',
    },
    usageTypeLabels: TENANT_USAGE_TYPE_LABELS,
    getUnitLabel: (_, isParking) => (isParking ? 'Stpl.' : 'EH'),
}

const getChangePercentage = (baseValue?: number, delta?: number) => {
    if (baseValue === undefined || delta === undefined) {
        return undefined
    }

    if (baseValue === 0) {
        if (delta === 0) {
            return 0
        }

        return delta > 0 ? 1 : -1
    }

    return delta / baseValue
}

const buildAreaGroup = (baseValue?: AreaValueDataObject, valueCheck?: AreaValueDataObject): GroupValues<AreaValueDataObject> => {
    if (!baseValue || !valueCheck) {
        return {
            baseValue,
            valueCheck,
            hasDiff: baseValue?.value !== valueCheck?.value,
        }
    }

    const delta = valueCheck.value - baseValue.value
    const change = getChangePercentage(baseValue.value, delta)
    return {
        baseValue,
        valueCheck,
        change,
        delta,
        hasDiff: delta !== 0,
    }
}

const buildNumberOfUnitsGroup = (baseValue?: number, valueCheck?: number): GroupValues<number> => {
    const delta = baseValue === undefined || valueCheck === undefined ? undefined : valueCheck - baseValue
    const change = getChangePercentage(baseValue, delta)
    return {
        baseValue,
        valueCheck,
        change,
        delta,
        hasDiff: baseValue !== valueCheck,
    }
}

const isDisplayableSummaryType = (type?: TenantSummaryItemValues['type']): type is Exclude<TenantUsageTypeLabelKeys, 'total'> =>
    !!type && type !== 'total' && type !== 'override_total'

const getSummaryTotalsFromTenantSummaryItem = (summary?: TenantSummaryItemValues) => {
    return {
        totalArea: summary?.total_area,
        vacancyArea: summary?.vacancy_area,
        totalUnits: summary?.number_of_units,
        vacancyUnits: summary?.vacancy_number_of_units,
    }
}

const getAreaDiff = (total?: AreaValueDataObject, vacancy?: AreaValueDataObject) =>
    total && vacancy ? subtractAreaValueDataObjects(total, vacancy) : undefined

const getNumberOfUnitsDiff = (total?: number, vacancy?: number) =>
    total !== undefined && vacancy !== undefined ? total - vacancy : undefined

const getVacancyPercentage = (row: ValueCheckTenantSummaryRow, valueKey: 'baseValue' | 'valueCheck') => {
    if (row.valueType === 'area') {
        const totalArea = row.groups.rentable[valueKey]
        const vacancyArea = row.groups.vacant[valueKey]
        if (!totalArea || !vacancyArea) {
            return undefined
        }
        if (totalArea.value === 0) {
            return vacancyArea.value === 0 ? 0 : undefined
        }
        return vacancyArea.value / totalArea.value
    }

    const totalUnits = row.groups.rentable[valueKey]
    const vacancyUnits = row.groups.vacant[valueKey]
    if (totalUnits === undefined || vacancyUnits === undefined) {
        return undefined
    }
    if (totalUnits === 0) {
        return vacancyUnits === 0 ? 0 : undefined
    }
    return vacancyUnits / totalUnits
}

const renderVacancyValue = (
    row: ValueCheckTenantSummaryRow,
    valueKey: 'baseValue' | 'valueCheck',
    copy: ValueCheckTenantSummaryWidgetCopy,
    locale = 'de-DE'
) => {
    if (row.valueType === 'area') {
        const vacancyValue = row.groups.vacant[valueKey]
        if (!vacancyValue) {
            return <EmptyCell textAlign="start" />
        }
        const areaValue = AreaValue.fromDataObject(vacancyValue)
        return (
            <Stack direction="row" gap={tokens.SizingSpacing075}>
                <Typography color={tokens.ColorTextBold} variant="textS">
                    {areaValue.format({ withUnit: false, minimumNumberOfDigits: 2, locale })}
                </Typography>
                <Typography color={tokens.ColorTextSubtle} variant="textS">
                    {areaValue.unitSymbol(locale)}
                </Typography>
            </Stack>
        )
    }

    const vacancyValue = row.groups.vacant[valueKey]
    if (vacancyValue === undefined) {
        return <EmptyCell textAlign="start" />
    }

    return (
        <UnitsSummaryCell
            numberOfUnits={vacancyValue}
            isParking={row.valueType === 'parking'}
            getUnitLabel={copy.getUnitLabel}
            locale={locale}
        />
    )
}

const renderGroupValue = (
    row: ValueCheckTenantSummaryRow,
    groupKey: SummaryGroupKey,
    valueKey: 'baseValue' | 'valueCheck',
    copy: ValueCheckTenantSummaryWidgetCopy,
    locale = 'de-DE'
) => {
    if (groupKey === 'vacant') {
        const vacancyPercentage = getVacancyPercentage(row, valueKey)
        return (
            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
                {renderVacancyValue(row, valueKey, copy, locale)}
                {vacancyPercentage !== undefined ? (
                    <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {new Percentage(vacancyPercentage).format({ withUnit: false, locale })}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextSubtle}>
                            %
                        </Typography>
                    </Stack>
                ) : (
                    <EmptyCell textAlign="end" />
                )}
            </Box>
        )
    }

    if (row.valueType === 'area') {
        const group = row.groups[groupKey]
        return <AreaSummaryCell area={group[valueKey]} align="start" locale={locale} />
    }

    const group = row.groups[groupKey]
    const numberValue = group[valueKey]
    if (numberValue === undefined) {
        return <EmptyCell textAlign="start" />
    }

    return (
        <Box width="100%" display="flex" justifyContent="flex-start">
            <UnitsSummaryCell
                numberOfUnits={numberValue}
                isParking={row.valueType === 'parking'}
                getUnitLabel={copy.getUnitLabel}
                locale={locale}
            />
        </Box>
    )
}

const hasAnyGroupValue = (row: ValueCheckTenantSummaryRow) =>
    summaryGroups.some((key) => {
        const group = row.groups[key]
        return group.baseValue !== undefined || group.valueCheck !== undefined
    })

const getNumericGroupValue = (row: ValueCheckTenantSummaryRow, key: SummaryGroupKey, valueKey: 'baseValue' | 'valueCheck') => {
    if (row.valueType === 'area') {
        return row.groups[key][valueKey]?.value
    }

    return row.groups[key][valueKey]
}

const getDefinedNumericValuesForSide = (row: ValueCheckTenantSummaryRow, valueKey: 'baseValue' | 'valueCheck') =>
    summaryGroups.map((key) => getNumericGroupValue(row, key, valueKey)).filter((value): value is number => value !== undefined)

const hasOnlyZeroValues = (values: number[]) => values.length > 0 && values.every((value) => value === 0)

const hasOnlyZeroValuesOnBothSides = (row: ValueCheckTenantSummaryRow) =>
    hasOnlyZeroValues(getDefinedNumericValuesForSide(row, 'baseValue')) &&
    hasOnlyZeroValues(getDefinedNumericValuesForSide(row, 'valueCheck'))

type ValueCheckTenantSummaryWidgetProps = {
    baseTenantSummary: TenantSummaryItemValues[]
    valueCheckTenantSummary: TenantSummaryItemValues[]
    baseValuationDate?: string
    valueCheckDate?: string
    copy?: ValueCheckTenantSummaryWidgetCopy
    locale?: string
}

export default function ValueCheckTenantSummaryWidget({
    baseTenantSummary,
    valueCheckTenantSummary,
    baseValuationDate,
    valueCheckDate,
    copy = DEFAULT_VALUE_CHECK_TENANT_SUMMARY_WIDGET_COPY,
    locale = 'de-DE',
}: ValueCheckTenantSummaryWidgetProps) {
    const filteredBaseSummaryItems = baseTenantSummary.filter((item) => isDisplayableSummaryType(item.type))
    const filteredValueCheckSummaryItems = valueCheckTenantSummary.filter((item) => isDisplayableSummaryType(item.type))

    const usageTypes: TenantUsageTypeLabelKeys[] = []
    filteredValueCheckSummaryItems.concat(filteredBaseSummaryItems).forEach(({ type }) => {
        if (!isDisplayableSummaryType(type) || isParkingType(type) || usageTypes.includes(type)) {
            return
        }
        usageTypes.push(type)
    })

    const parkingBaseItem = filteredBaseSummaryItems.find((item) => isDisplayableSummaryType(item.type) && isParkingType(item.type))
    const parkingValueCheckItem = filteredValueCheckSummaryItems.find(
        (item) => isDisplayableSummaryType(item.type) && isParkingType(item.type)
    )

    const rows: ValueCheckTenantSummaryRow[] = [
        ...usageTypes.flatMap((usageType) => {
            const baseItem = filteredBaseSummaryItems.find((item) => item.type === usageType)
            const valueCheckItem = filteredValueCheckSummaryItems.find((item) => item.type === usageType)
            const baseTotals = getSummaryTotalsFromTenantSummaryItem(baseItem)
            const valueCheckTotals = getSummaryTotalsFromTenantSummaryItem(valueCheckItem)
            const usageTypeLabel = copy.usageTypeLabels[usageType]

            return [
                {
                    label: `${copy.rowLabels.area} - ${usageTypeLabel}`,
                    valueType: 'area' as const,
                    groups: {
                        rentable: buildAreaGroup(baseTotals.totalArea, valueCheckTotals.totalArea),
                        rented: buildAreaGroup(
                            getAreaDiff(baseTotals.totalArea, baseTotals.vacancyArea),
                            getAreaDiff(valueCheckTotals.totalArea, valueCheckTotals.vacancyArea)
                        ),
                        vacant: buildAreaGroup(baseTotals.vacancyArea, valueCheckTotals.vacancyArea),
                    },
                },
                {
                    label: `${copy.rowLabels.units} - ${usageTypeLabel}`,
                    valueType: 'units' as const,
                    groups: {
                        rentable: buildNumberOfUnitsGroup(baseTotals.totalUnits, valueCheckTotals.totalUnits),
                        rented: buildNumberOfUnitsGroup(
                            getNumberOfUnitsDiff(baseTotals.totalUnits, baseTotals.vacancyUnits),
                            getNumberOfUnitsDiff(valueCheckTotals.totalUnits, valueCheckTotals.vacancyUnits)
                        ),
                        vacant: buildNumberOfUnitsGroup(baseTotals.vacancyUnits, valueCheckTotals.vacancyUnits),
                    },
                },
            ]
        }),
        {
            label: copy.rowLabels.parkingSpaces,
            valueType: 'parking' as const,
            groups: {
                rentable: buildNumberOfUnitsGroup(parkingBaseItem?.number_of_units, parkingValueCheckItem?.number_of_units),
                rented: buildNumberOfUnitsGroup(
                    getNumberOfUnitsDiff(parkingBaseItem?.number_of_units, parkingBaseItem?.vacancy_number_of_units),
                    getNumberOfUnitsDiff(parkingValueCheckItem?.number_of_units, parkingValueCheckItem?.vacancy_number_of_units)
                ),
                vacant: buildNumberOfUnitsGroup(parkingBaseItem?.vacancy_number_of_units, parkingValueCheckItem?.vacancy_number_of_units),
            },
        },
    ].filter((row) => hasAnyGroupValue(row) && !hasOnlyZeroValuesOnBothSides(row))

    const columns = [
        columnHelper.display({
            id: 'category',
            header: () => <TenantSummaryHeaderCell text={copy.categoryLabel} />,
            cell: ({ row: { original } }) => (
                <SummaryCellContainer>
                    <Box width="100%" textAlign="start">
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {original.label}
                        </Typography>
                    </Box>
                </SummaryCellContainer>
            ),
        }),
        ...summaryGroups.flatMap((key) => {
            return [
                columnHelper.display({
                    id: `${key}-base`,
                    header: () => (
                        <TenantSummaryHeaderCell
                            subheader={baseValuationDate ? dayjs(baseValuationDate).format('YYYY') : copy.baseLabel}
                            text={copy.groupLabels[key]}
                            showLeftBorder={shouldShowGroupSeparator(key)}
                        />
                    ),
                    cell: ({ row: { original } }) => (
                        <SummaryCellContainer>{renderGroupValue(original, key, 'baseValue', copy, locale)}</SummaryCellContainer>
                    ),
                }),
                columnHelper.display({
                    id: `${key}-check`,
                    header: () => (
                        <TenantSummaryHeaderCell subheader={valueCheckDate ? dayjs(valueCheckDate).format('YYYY') : copy.valueCheckLabel} />
                    ),
                    cell: ({ row: { original } }) => {
                        return (
                            <SummaryCellContainer state={original.groups[key].hasDiff ? 'warning' : undefined}>
                                {renderGroupValue(original, key, 'valueCheck', copy, locale)}
                            </SummaryCellContainer>
                        )
                    },
                }),
                columnHelper.display({
                    id: `${key}-change`,
                    header: () => <TenantSummaryHeaderCell subheader={copy.changeLabel} />,
                    cell: ({ row: { original } }) => {
                        const group = original.groups[key]
                        const delta = group.delta ?? 0
                        const isVacantGroup = key === 'vacant'
                        let state: 'positive' | 'negative' | 'warning' | undefined

                        if (!group.hasDiff) {
                            state = undefined
                        } else if (delta === 0) {
                            state = 'warning'
                        } else if (isVacantGroup) {
                            state = delta > 0 ? 'negative' : 'positive'
                        } else {
                            state = delta > 0 ? 'positive' : 'negative'
                        }
                        return (
                            <SummaryCellContainer state={state}>
                                <Box width="100%" display="flex" justifyContent="flex-end">
                                    {group.change !== undefined ? (
                                        <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                            <Typography variant="textS">
                                                {new Percentage(group.change).format({
                                                    withSign: true,
                                                    withSpaceAfterSign: true,
                                                    numberOfDigits: 2,
                                                    withUnit: false,
                                                    locale,
                                                })}
                                            </Typography>
                                            <Typography variant="textS" color={tokens.ColorTextSubtle}>
                                                %
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Typography variant="textS">-</Typography>
                                    )}
                                </Box>
                            </SummaryCellContainer>
                        )
                    },
                }),
            ]
        }),
    ]

    return (
        <Stack direction="column" gap={tokens.SizingSpacing200}>
            <Headline title={copy.title} variant="headline3" />
            <Table
                columns={columns}
                data={rows}
                greyThemed
                columnPinning={{ left: ['category'] }}
                width="100%"
                withZeroCellPadding
                withTableSpacing
            />
        </Stack>
    )
}
